import type { ProjectRole } from '../types/api';

export interface SelectableProject {
  id: string;
  role: ProjectRole | null;
  /** apps.key this project belongs to. Used to detect a stale cross-app selection. */
  app_key: string;
}

export type ReconcileAction =
  | { type: 'auto-select'; id: string; role: ProjectRole | null }
  | { type: 'sync-role'; id: string; role: ProjectRole | null }
  /** Persisted selection belongs to a different app than the active one, and
   *  no project in the active app is available to fall back to. */
  | { type: 'clear' };

/**
 * Decide how to reconcile the persisted project selection against the freshly
 * loaded projects list. Pure so it can be unit-tested without rendering.
 *
 *  - Persisted id no longer in the list (removed from project / soft-deleted):
 *    auto-select the first available project so the app recovers instead of
 *    403-looping on a stale `X-Project-Id` (FE-B3).
 *  - Persisted id belongs to a different app than the active one (e.g. stale
 *    localStorage from before per-app scoping, or a multi-tab race): treat it
 *    like a missing selection — auto-select the first project in the active
 *    app, or clear the selection if there isn't one.
 *  - Persisted id still valid but the role drifted since last load: sync it.
 *  - Otherwise: nothing to do.
 */
export function reconcileProjectSelection(args: {
  projectId: string | null;
  projectRole: ProjectRole | null;
  projects: SelectableProject[];
  isLoading: boolean;
  appKey: string | null;
}): ReconcileAction | null {
  const { projectId, projectRole, projects, isLoading, appKey } = args;
  if (isLoading || projectId == null) return null;

  const selected = projects.find((p) => p.id === projectId);
  const appMismatch = selected != null && appKey != null && selected.app_key !== appKey;

  if (!selected || appMismatch) {
    const fallback = projects.find((p) => appKey == null || p.app_key === appKey);
    if (fallback) return { type: 'auto-select', id: fallback.id, role: fallback.role };
    // No project in the active app to fall back to. A plain "not found"
    // selection is left as-is (empty-state UI handles it); an app-mismatched
    // one is actively cleared so a stale wrong-app id doesn't keep going out
    // as X-Project-Id.
    return appMismatch ? { type: 'clear' } : null;
  }

  const freshRole = selected.role ?? null;
  if (freshRole !== projectRole) {
    return { type: 'sync-role', id: projectId, role: freshRole };
  }
  return null;
}
